// @ts-strict-ignore
import { CompressionSimpleTextValueVO, MetaElementUsageIdentityVO, SimpleTextValueVO, TableTextItemVO, TableTextRowVO, TableTextValueVO, TextureAreaUsageVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { LabelStripTexture, LabelStripTextureStorage } from './replace-labeling-data';
import { Injectable } from '@angular/core';

class SplitRow {
  public headItems: TableTextItemVO[] = [];
  public tailItems: TableTextItemVO[] = [];

  public addHeadItem(item: TableTextItemVO): void {
    this.headItems.push(item);
  }

  public addTailItem(item: TableTextItemVO): void {
    this.tailItems.push(item);
  }
}

@Injectable({providedIn: 'root'})
export class ReplaceLabelingTextureValidator {

  public applyTextValue(from: LabelStripTexture, fromSource: LabelStripTextureStorage,
                        to: LabelStripTexture, toSource: LabelStripTextureStorage): void {
    const fromUsage: TextureAreaUsageVO = from.usage;
    const toUsage: TextureAreaUsageVO = to.usage;

    toUsage.textValue = fromUsage.textValue;
    toUsage.previousMergedUsageId = this.getTextureByBackwardId(fromUsage.previousMergedUsageId, fromSource, toSource);
    toUsage.nextMergedUsageId = this.getTextureByBackwardId(fromUsage.nextMergedUsageId, fromSource, toSource);
  }

  private getTextureByBackwardId(fromId: MetaElementUsageIdentityVO, fromStorage: LabelStripTextureStorage, toStorage: LabelStripTextureStorage): MetaElementUsageIdentityVO {
    if (!fromId) {
      return null;
    }

    const fromTexture: LabelStripTexture = fromStorage.getTextureByIdentity(fromId);
    const toTexture: LabelStripTexture = toStorage.getTextureByBackwardId(fromTexture.backwardAcceptingId);
    return toTexture.identity;
  }

  /**
   * Updates references to merged connections and corresponding label-text basing on new area added right after the neighbor connection.
   * Possible cases are handled in the following way:
   * 1. Neighbor connection is not merged with any other connection - new texture adopts multi-line count from neighbor connection and
   * has its own cells which are not merged with anyone.
   * 2. Neighbor connection is merged only with previous connection - new texture gets appended (merged) to these connections,
   * so that merged cells gets extended (col-span gets increased).
   * 3. Neighbor connection is merged with next connection - new texture gets inserted in the middle of this union.
   *
   * In case label-text which needs to be updated is a table - approach described above applied to each row in the table.
   *
   * @param newTexture newly created texture usage (cell) which needs to be linked with existing cells
   * @param addAfterTexture neighbor texture usage after which new cell needs to be added
   * @param source source article which contains given cells
   */
  public addNewTextureAfter(newTexture: LabelStripTexture, addAfterTexture: LabelStripTexture, source: LabelStripTextureStorage): void {
    if (addAfterTexture.usage.previousMergedUsageId || addAfterTexture.usage.nextMergedUsageId) {
      // text of merged connections is stored in the very first connection, so find it to retrieve text-value which needs to be updated by new texture-usage (column)
      const firstMergedTexture: LabelStripTexture = this.findFirstMergedTexture(addAfterTexture, source);
      const mergedTextValue: TextValueVO = firstMergedTexture.usage.textValue;
      if (mergedTextValue instanceof TableTextValueVO) {
        this.addNewColumnToTable(mergedTextValue, this.getTextureAreaIndex(addAfterTexture.identity, firstMergedTexture, source) + 1);
      }

      // retrieve original next usage before changing reference to the new usage
      const originalNextTexture: LabelStripTexture = this.findNextMergedTexture(addAfterTexture, source);
      newTexture.usage.previousMergedUsageId = addAfterTexture.identity;
      addAfterTexture.usage.nextMergedUsageId = newTexture.identity;
      if (originalNextTexture) {
        newTexture.usage.nextMergedUsageId = originalNextTexture.identity;
        originalNextTexture.usage.previousMergedUsageId = newTexture.identity;
      }
    } else if (addAfterTexture.usage.textValue instanceof TableTextValueVO) {
      this.adoptMultiLineCount(newTexture, addAfterTexture.usage.textValue);
    }
  }

  private addNewColumnToTable(table: TableTextValueVO, addAtIndex: number): void {
    for (const row of table.rows) {
      this.addNewColumnToRow(row, addAtIndex);
    }
  }

  private addNewColumnToRow(row: TableTextRowVO, addAtIndex: number): void {
    let colCount: number = 0;
    for (let i: number = 0; i < row.items.length; i++) {
      const item: TableTextItemVO = row.items[i];
      colCount += item.colSpan;

      if (addAtIndex === colCount) {
        // new row-item has to be added right after the existing one
        if (item.colSpan > 1) {
          // existing item is merged - merge new item with it as well then (increase col-span of already existing item)
          item.colSpan++;
        } else {
          // existing item is not merged - add single item which is not merged with anyone
          const newItem: TableTextItemVO = this.createTableTextItem(1, item.rowSpan);
          row.items.splice(i + 1, 0, newItem);
        }

        break;
      } else if (addAtIndex < colCount) {
        // new row-item has to be added in-between already merged items - extend merged area by increasing col-span
        item.colSpan++;

        break;
      }
    }
  }

  private adoptMultiLineCount(texture: LabelStripTexture, sourceTextValue: TableTextValueVO): void {
    texture.usage.textValue = sourceTextValue.clone();
    (texture.usage.textValue as TableTextValueVO).rows.forEach((row: TableTextRowVO) => {
      row.items.forEach((item: TableTextItemVO) => item.value = this.createSimpleTextValue());
    });
  }

  public removeTexture(textureToDelete: LabelStripTexture, source: LabelStripTextureStorage): void {
    const previousTexture: LabelStripTexture = this.findPreviousMergedTexture(textureToDelete, source);
    const nextNextTexture: LabelStripTexture = this.findNextMergedTexture(textureToDelete, source);
    if (!previousTexture && !nextNextTexture) {
      return;
    }

    const firstMergedTexture: LabelStripTexture = this.findFirstMergedTexture(textureToDelete, source);
    const mergedTextValue: TextValueVO = firstMergedTexture.usage.textValue;
    if (mergedTextValue instanceof TableTextValueVO) {
      this.removeColumnFromTable(mergedTextValue, this.getTextureAreaIndex(textureToDelete.identity, firstMergedTexture, source));
    }

    if (previousTexture && nextNextTexture) {
      previousTexture.usage.nextMergedUsageId = nextNextTexture.identity;
      nextNextTexture.usage.previousMergedUsageId = previousTexture.identity;
    } else if (previousTexture && !nextNextTexture) {
      previousTexture.usage.nextMergedUsageId = null;
    } else if (nextNextTexture && !previousTexture) {
      nextNextTexture.usage.textValue = mergedTextValue;
      nextNextTexture.usage.previousMergedUsageId = null;
    }
    textureToDelete.usage.previousMergedUsageId = null;
    textureToDelete.usage.nextMergedUsageId = null;
    textureToDelete.usage.textValue = this.createSimpleTextValue();
  }

  private removeColumnFromTable(table: TableTextValueVO, removeAtIndex: number): void {
    for (const row of table.rows) {
      this.removeColumnFromRow(row, removeAtIndex);
    }
  }

  private removeColumnFromRow(row: TableTextRowVO, removeAtIndex: number): void {
    let colCount: number = 0;
    for (let i: number = 0; i < row.items.length; i++) {
      const item: TableTextItemVO = row.items[i];
      const itemStartIndex: number = colCount;
      const itemEndIndex: number = colCount + item.colSpan;
      if (removeAtIndex >= itemStartIndex && removeAtIndex < itemEndIndex) {
        if (item.colSpan > 1) {
          item.colSpan--;
        } else {
          row.items.splice(i, 1);
        }

        break;
      }

      colCount = itemEndIndex;
    }
  }

  public splitTextureAfter(splitAfterTexture: LabelStripTexture, source: LabelStripTextureStorage): void {
    if (!splitAfterTexture.usage.nextMergedUsageId) {
      return;
    }

    const firstMergedTexture: LabelStripTexture = this.findFirstMergedTexture(splitAfterTexture, source);
    const mergedTextValue: TextValueVO = firstMergedTexture.usage.textValue;
    const allMergedTextures: LabelStripTexture[] = this.findNextMergedTextures(firstMergedTexture, source);
    const splitIndex: number = this.getTextureAreaIndexInArray(splitAfterTexture.identity, allMergedTextures) + 1;
    if (mergedTextValue instanceof TableTextValueVO) {
      this.splitTableTextValue(mergedTextValue, allMergedTextures, splitIndex);
    } else if (mergedTextValue instanceof SimpleTextValueVO) {
      this.splitSimpleTextValue(allMergedTextures, splitIndex);
    }

    const nextTexture: LabelStripTexture = this.findNextMergedTexture(splitAfterTexture, source);
    splitAfterTexture.usage.nextMergedUsageId = null;
    nextTexture.usage.previousMergedUsageId = null;
  }

  private splitTableTextValue(table: TableTextValueVO, allMergedTextures: LabelStripTexture[], splitIndex: number): void {
    if (splitIndex <= 0 || splitIndex >= allMergedTextures.length) {
      return;
    }

    const headTable: TableTextValueVO = new TableTextValueVO();
    const tailTable: TableTextValueVO = new TableTextValueVO();

    for (const row of table.rows) {
      const splitRow: SplitRow = this.splitRow(row, allMergedTextures, splitIndex);
      this.addRowToTable(headTable, splitRow.headItems);
      this.addRowToTable(tailTable, splitRow.tailItems);
    }

    const headSourceTexture: LabelStripTexture = allMergedTextures[0];
    headSourceTexture.usage.textValue = headTable;
    const tailSourceTexture: LabelStripTexture = allMergedTextures[splitIndex];
    tailSourceTexture.usage.textValue = tailTable;
  }

  private splitRow(row: TableTextRowVO, allMergedTextures: LabelStripTexture[], splitIndex: number): SplitRow {
    const result: SplitRow = new SplitRow();

    let colCount: number = 0;
    for (let i: number = 0; i < row.items.length; i++) {
      const item: TableTextItemVO = row.items[i];
      const itemStartIndex: number = colCount;
      const itemEndIndex: number = colCount + item.colSpan;

      if (splitIndex > itemStartIndex && splitIndex < itemEndIndex) {
        // split current row-item by reducing col-span basing on split-index
        item.colSpan = splitIndex - itemStartIndex;

        // create additional row-item for remaining col-spans
        const tailItem: TableTextItemVO = this.createTableTextItem(itemEndIndex - splitIndex, item.rowSpan);

        if (this.shouldMoveTextToTail(allMergedTextures, itemStartIndex, itemEndIndex, splitIndex)) {
          tailItem.value = item.value;
          item.value = this.createSimpleTextValue();
        }

        result.addHeadItem(item);
        result.addTailItem(tailItem);
      } else if (splitIndex >= itemEndIndex) {
        result.addHeadItem(item);
      } else {
        result.addTailItem(item);
      }

      colCount = itemEndIndex;
    }

    return result;
  }

  private addRowToTable(table: TableTextValueVO, items: TableTextItemVO[]) {
    const row: TableTextRowVO = new TableTextRowVO();
    row.items.push(...items);
    table.rows.push(row);
  }

  private splitSimpleTextValue(allMergedTextures: LabelStripTexture[], splitIndex: number): void {
    const headSourceTexture: LabelStripTexture = allMergedTextures[0];
    const tailSourceTexture: LabelStripTexture = allMergedTextures[splitIndex];
    if (this.shouldMoveTextToTail(allMergedTextures, 0, allMergedTextures.length, splitIndex)) {
      tailSourceTexture.usage.textValue = headSourceTexture.usage.textValue;
      headSourceTexture.usage.textValue = this.createSimpleTextValue();
    } else {
      tailSourceTexture.usage.textValue = this.createSimpleTextValue();
    }
  }

  /**
   * Checks if text-value specified for merged connections needs to be moved to the tail part after the split.
   * Such "movement" has to be done in case length of tail part is bigger than head part of merged connections, otherwise text is kept on the original connection.
   */
  private shouldMoveTextToTail(allMergedTextures: LabelStripTexture[],
                               startIndex: number, endIndex: number, splitIndex: number): boolean {
    const textureBefore: LabelStripTexture[] = allMergedTextures.slice(startIndex, splitIndex);
    const textureAfter: LabelStripTexture[] = allMergedTextures.slice(splitIndex, endIndex);
    return MathUtils.compare(this.getTextureWidth(textureAfter), this.getTextureWidth(textureBefore)) > 0;
  }

  private getTextureAreaIndex(textureIdentity: MetaElementUsageIdentityVO, firstMergedTexture: LabelStripTexture, source: LabelStripTextureStorage): number {
    const allMergedTextures: LabelStripTexture[] = this.findNextMergedTextures(firstMergedTexture, source);
    return this.getTextureAreaIndexInArray(textureIdentity, allMergedTextures);
  }

  private getTextureAreaIndexInArray(textureIdentity: MetaElementUsageIdentityVO, source: LabelStripTexture[]): number {
    for (let i: number = 0; i < source.length; i++) {
      const texture: LabelStripTexture = source[i];
      if (textureIdentity.equals(texture.identity)) {
        return i;
      }
    }
    return NaN;
  }

  private findFirstMergedTexture(texture: LabelStripTexture, source: LabelStripTextureStorage): LabelStripTexture {
    if (!texture || !texture.usage.previousMergedUsageId) {
      return texture;
    }
    let previousTexture: LabelStripTexture = this.findPreviousMergedTexture(texture, source);
    const previousTextures: LabelStripTexture[] = [];
    while (previousTexture) {
      previousTextures.push(previousTexture);
      previousTexture = this.findPreviousMergedTexture(previousTexture, source);
    }

    return previousTextures[previousTextures.length - 1];
  }

  private findPreviousMergedTexture(startTexture: LabelStripTexture, source: LabelStripTextureStorage): LabelStripTexture {
    if (!startTexture) {
      return null;
    }

    const previousTextureId: MetaElementUsageIdentityVO = startTexture.usage.previousMergedUsageId;
    if (!previousTextureId) {
      return null;
    }

    return this.findTextureByIdentity(previousTextureId, source);
  }

  private findNextMergedTextures(texture: LabelStripTexture, source: LabelStripTextureStorage): LabelStripTexture[] {
    if (!texture) {
      return [];
    }

    const mergedTextures: LabelStripTexture[] = [texture];
    let nextTexture: LabelStripTexture = this.findNextMergedTexture(texture, source);
    while (nextTexture) {
      mergedTextures.push(nextTexture);
      nextTexture = this.findNextMergedTexture(nextTexture, source);
    }

    return mergedTextures;
  }

  private findNextMergedTexture(startTexture: LabelStripTexture, source: LabelStripTextureStorage): LabelStripTexture {
    if (!startTexture) {
      return null;
    }

    const nextTextureId: MetaElementUsageIdentityVO = startTexture.usage.nextMergedUsageId;
    if (!nextTextureId) {
      return null;
    }

    return this.findTextureByIdentity(nextTextureId, source);
  }

  private findTextureByIdentity(identity: MetaElementUsageIdentityVO, source: LabelStripTextureStorage): LabelStripTexture {
    return !identity ? null : source.getTextureByIdentity(identity);
  }

  private getTextureWidth(textures: LabelStripTexture[]): number {
    if (ArrayUtils.isEmpty(textures)) {
      return 0;
    }

    const startTexture: LabelStripTexture = textures[0];
    const endTexture: LabelStripTexture = textures[textures.length - 1];
    return !startTexture.localRectangle || !endTexture.localRectangle ? 0 : startTexture.localRectangle.point1.distanceTo(endTexture.localRectangle.point4);
  }

  private createTableTextItem(colSpan: number, rowSpan: number): TableTextItemVO {
    const item: TableTextItemVO = new TableTextItemVO();
    item.colSpan = colSpan;
    item.rowSpan = rowSpan;
    item.value = this.createSimpleTextValue();
    return item;
  }

  private createSimpleTextValue(): SimpleTextValueVO {
    return new CompressionSimpleTextValueVO();
  }
}
